import MarketingLayout from '../../components/marketing/MarketingLayout';
import { Card, Title, Text } from "@tremor/react";
import { useState, useEffect } from 'react';
import BookingWidget from '../../components/BookingWidget';
import ServicePageNavigation from '../../components/ServicePageNavigation';
import { colors } from '../../lib/colors';
import { scrollToTop } from '../../utils/scrollUtils';

export default function DisabilityInsurance() {
  const [showBookingWidget, setShowBookingWidget] = useState(false);
  
  // Ensure scroll position is reset to top when component mounts
  useEffect(() => {
    scrollToTop();
  }, []);

  return (
    <MarketingLayout>
      <section className="py-16 px-4 bg-white dark:bg-gray-900">
        <div className="max-w-4xl mx-auto relative">
          {/* Service Navigation */}
          <ServicePageNavigation currentSlug="disability-insurance" />
          {/* Header */}
          <div className="text-center mb-12 pt-4"> {/* Added padding top */}
            <Title className="text-4xl font-bold text-gray-800 dark:text-white mb-4">Disability Insurance</Title>
            <Text className="text-xl text-gray-600 dark:text-gray-400">
              Protecting Your Most Valuable Asset: Your Ability to Earn an Income
            </Text>
          </div>

          <div className="space-y-8">
            {/* What is Disability Insurance? */}
            <Card className="p-6 bg-gray-50 dark:bg-gray-800 shadow-lg">
              <Title className="text-2xl font-semibold text-gray-800 dark:text-white mb-3">What is Disability Insurance?</Title>
              <Text className="text-gray-600 dark:text-gray-300">
                Disability insurance (DI), often called income protection, provides a regular monthly income replacement benefit if you are unable to work due to a qualifying illness or injury. It's designed to help you cover living expenses, pay bills, and maintain your financial stability when your regular paycheque stops.
              </Text>
              <Text className="text-gray-600 dark:text-gray-300 mt-2">
                Your ability to earn an income is often your most significant financial asset. Disability insurance acts as a safety net, protecting that asset and preventing a health issue from turning into a devastating financial crisis.
              </Text>
            </Card>

            {/* How It Works */}
            <Card className="p-6 bg-gray-50 dark:bg-gray-800 shadow-lg">
              <Title className="text-2xl font-semibold text-gray-800 dark:text-white mb-3">How It Works</Title>
              <ol className="list-decimal list-inside text-gray-600 dark:text-gray-300 space-y-2 mt-4">
                <li>
                  <strong>Policy Selection:</strong> You choose a policy based on factors like the monthly benefit amount (typically a percentage of your income), the waiting period (elimination period) before benefits start, the benefit period (how long benefits last), and the definition of disability (e.g., "own occupation" vs. "any occupation").
                </li>
                <li>
                  <strong>Pay Premiums:</strong> You pay regular premiums to keep the policy in force.
                </li>
                <li>
                  <strong>Disability Occurs:</strong> If you suffer an illness or injury that prevents you from working according to the policy's definition, you file a claim.
                </li>
                <li>
                  <strong>Waiting Period:</strong> After filing the claim, you must satisfy the waiting period (e.g., 30, 60, 90, 120 days) during which no benefits are paid.
                </li>
                <li>
                  <strong>Receive Benefits:</strong> Once the waiting period is met and the claim is approved, you begin receiving the monthly disability benefit payments. Benefits are typically received tax-free if you paid the premiums personally with after-tax dollars.
                </li>
                 <li>
                  <strong>Benefit Period:</strong> Benefits continue for the duration specified in the policy (e.g., 2 years, 5 years, or until age 65), as long as you remain disabled according to the policy terms.
                </li>
              </ol>
            </Card>

            {/* Key Benefits */}
            <Card className="p-6 bg-gray-50 dark:bg-gray-800 shadow-lg">
              <Title className="text-2xl font-semibold text-gray-800 dark:text-white mb-3">Key Benefits</Title>
              <ul className="list-disc list-inside text-gray-600 dark:text-gray-300 space-y-1">
                <li><strong>Income Replacement:</strong> Provides a crucial source of funds when you cannot work, helping maintain your lifestyle.</li>
                <li><strong>Financial Stability:</strong> Helps cover essential expenses like mortgage/rent, utilities, food, and loan payments.</li>
                <li><strong>Protection of Savings:</strong> Prevents the need to deplete savings, investments, or retirement funds during a period of disability.</li>
                <li><strong>Peace of Mind:</strong> Reduces financial stress during a challenging health situation, allowing you to focus on recovery.</li>
                <li><strong>Customizable Coverage:</strong> Policies can be tailored with riders (optional add-ons) for specific needs like cost-of-living adjustments or partial disability benefits.</li>
              </ul>
            </Card>

            {/* Considerations */}
            <Card className="p-6 bg-gray-50 dark:bg-gray-800 shadow-lg">
              <Title className="text-2xl font-semibold text-gray-800 dark:text-white mb-3">Considerations</Title>
               <Text className="text-gray-600 dark:text-gray-300 mb-3">Important factors to evaluate:</Text>
              <ul className="list-disc list-inside text-gray-600 dark:text-gray-300 space-y-1">
                <li><strong>Definition of Disability:</strong> Crucial difference between "own occupation" (unable to do your specific job) and "any occupation" (unable to do any job you're suited for). "Own occupation" offers stronger protection.</li>
                <li><strong>Waiting Period:</strong> A longer waiting period typically means lower premiums, but requires having sufficient emergency funds.</li>
                <li><strong>Benefit Period:</strong> Longer benefit periods (e.g., to age 65) provide more comprehensive protection but cost more.</li>
                <li><strong>Benefit Amount:</strong> Usually capped at 60-85% of pre-tax income to incentivize returning to work.</li>
                <li><strong>Group vs. Individual:</strong> Group plans through work are often less comprehensive and may not be portable if you leave your job. Individual plans offer more control and customization.</li>
                <li><strong>Exclusions & Limitations:</strong> Policies have specific conditions that are not covered.</li>
              </ul>
            </Card>

            {/* Suitability */}
            <Card className="p-6 bg-gray-50 dark:bg-gray-800 shadow-lg">
              <Title className="text-2xl font-semibold text-gray-800 dark:text-white mb-3">Is Disability Insurance Right for You?</Title>
               <Text className="text-gray-600 dark:text-gray-300 mb-3">Disability insurance is essential for almost anyone who relies on their income to support themselves or their family, including:</Text>
              <ul className="list-disc list-inside text-gray-600 dark:text-gray-300 space-y-1">
                <li>Employees (especially if group coverage is inadequate or non-existent).</li>
                <li>Self-employed individuals and business owners.</li>
                <li>Professionals (doctors, lawyers, engineers, etc.).</li>
                <li>Primary income earners for a household.</li>
                <li>Anyone without significant passive income or savings to cover expenses during a long-term illness or injury.</li>
              </ul>
            </Card>

            {/* Call to Action */}
            <div className="text-center mt-12">
              <Title className="text-2xl font-semibold text-gray-800 dark:text-white mb-4">Protect Your Income, Secure Your Future</Title>
              <Text className="text-gray-600 dark:text-gray-400 mb-6 max-w-xl mx-auto">
                Don't let an unexpected illness or injury jeopardize your financial well-being. Let's design a disability insurance plan that fits your needs and budget.
              </Text>
              <button
                onClick={() => setShowBookingWidget(true)}
                className="px-8 py-3 rounded-md text-lg font-semibold text-white transition-transform duration-150 ease-in-out hover:scale-105 active:scale-95"
                style={{ background: `linear-gradient(135deg, ${colors.brand.primary} 0%, ${colors.brand.accent} 100%)` }}
              >
                Discuss Income Protection
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Booking Widget Modal */}
      {showBookingWidget && (
        <BookingWidget onClose={() => setShowBookingWidget(false)} />
      )}
    </MarketingLayout>
  );
}
